/**
 * What the firm charges, and the versions of it that came before.
 *
 * **This section never edits a tariff.** It introduces a new version with an effective date,
 * and the previous one closes at the same instant. Editing the numbers on a version that has
 * already priced rides would reprice them — every historical report becomes fiction and a
 * receipt reprinted next year disagrees with what somebody paid. That is the whole reason
 * tariffs are a table rather than columns on the tenant, and a UI is exactly where somebody
 * would otherwise try it. There is therefore no edit control on a row, only the button that
 * introduces the next version.
 *
 * The form starts pre-filled from the version in force, because a price change is almost
 * always "the same as now, but the per-kilometre goes up ten stotinki" rather than a fresh
 * set of numbers.
 *
 * It saves itself, from the dialog, so the page's save bar has nothing to do with it.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { blankTariff } from './blankTariff';
import { NewTariffDialog } from './NewTariffDialog';
import { settingsApi, type Tariff, type TariffDraft } from '../api';
import { refusal } from '../errors';
import { named } from '../check';
import { useTenantFormats } from '../../shell/formats';
import { vehicleChoicesApi, type VehicleClass } from '../../vehicles/api';
import { className } from '../../vehicles/words';
import {
  Banner,
  Button,
  EmptyState,
  FieldErrors,
  Icon,
  Pill,
  Section,
  Sub,
  Table,
  Td,
  Th,
  Tr,
  type FieldProblem,
} from '../../ui';

export function TariffSection() {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const currency = formats.currency;

  const [tariffs, setTariffs] = useState<Tariff[] | null>(null);
  const [draft, setDraft] = useState<TariffDraft>(() => blankTariff(currency));
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<FieldProblem | null>(null);

  /** The firm's classes, to name a tariff's class in words and to choose one for a new tariff. */
  const [classes, setClasses] = useState<VehicleClass[]>([]);

  const load = useCallback(async () => {
    try {
      const all = await settingsApi.tariffs();

      setTariffs(all);
      setClasses((await vehicleChoicesApi.get()).classes);

      // Pre-filled from whatever is in force, so raising one rate does not mean retyping
      // eight. A firm with no tariff at all gets the blank, which is the starter shape.
      const current = all.find((tariff) => tariff.effectiveTo === null);

      if (current !== undefined) {
        setDraft({
          code: current.code,
          displayName: current.displayName,
          vehicleCategoryCode: current.vehicleCategoryCode,
          effectiveFrom: null,
          baseFare: current.baseFare,
          perKilometre: current.perKilometre,
          perMinute: current.perMinute,
          perWaitingMinute: current.perWaitingMinute,
          freeWaitingMinutes: current.freeWaitingMinutes,
          minimumFare: current.minimumFare,
          currency: current.currency,
          roundingIncrement: current.roundingIncrement,
        });
      } else {
        setDraft(blankTariff(currency));
      }
    } catch (failure) {
      setError(refusal(failure, t, 'settings.couldNotLoad'));
    }
  }, [t, currency]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  useEffect(() => {
    if (!saved) return undefined;

    const timer = setTimeout(() => setSaved(false), 4000);

    return () => clearTimeout(timer);
  }, [saved]);

  async function introduce() {
    setSaving(true);
    setError(null);

    try {
      await settingsApi.introduceTariff(draft);
      await load();

      // Only now: a dialog closed on the request rather than on its answer takes the typed
      // numbers with it, and the person retypes all eight.
      setOpen(false);
      setSaved(true);
    } catch (failure) {
      setError(named(refusal(failure, t, 'settings.couldNotSave'), null, t));
    } finally {
      setSaving(false);
    }
  }

  function change<K extends keyof TariffDraft>(key: K, value: TariffDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  return (
    <FieldErrors problem={error} onEdit={() => setError(null)}>
      <Section
        title={t('tariffs.history')}
        description={t('tariffs.historyHelp')}
        actions={
          <Button size="sm" onClick={() => setOpen(true)}>
            <Icon name="plus" small />
            {t('tariffs.introduce')}
          </Button>
        }
      >
        {saved && (
          <div className="px-5 pt-3">
            <Banner tone="ok">{t('settings.saved')}</Banner>
          </div>
        )}

        {/* Where the panel's `p.error` was: the top of the section, under its heading. While
            the dialog is open it carries the error instead, because a modal covers this. */}
        {error !== null && !open && (
          <div className="px-5 pt-3">
            <Banner tone="danger">{error.message}</Banner>
          </div>
        )}

        {tariffs === null ? (
          <EmptyState>{t('app.loading')}</EmptyState>
        ) : tariffs.length === 0 ? (
          <EmptyState>{t('tariffs.none')}</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>{t('tariffs.code')}</Th>
                  <Th>{t('tariffs.from')}</Th>
                  <Th>{t('tariffs.to')}</Th>
                  <Th numeric>{t('tariffs.baseFare')}</Th>
                  <Th numeric>{t('tariffs.perKilometre')}</Th>
                  <Th numeric>{t('tariffs.perMinute')}</Th>
                  <Th numeric>{t('tariffs.minimumFare')}</Th>
                </tr>
              </thead>
              <tbody>
                {tariffs.map((tariff) => (
                  <Tr key={tariff.id} selected={tariff.effectiveTo === null}>
                    <Td>
                      {tariff.displayName}
                      {tariff.vehicleCategoryCode !== null && (
                        <Sub>
                          {' · '}
                          {className(
                            t,
                            tariff.vehicleCategoryCode,
                            classes.find((c) => c.code === tariff.vehicleCategoryCode)?.displayName,
                          )}
                        </Sub>
                      )}
                    </Td>
                    {/* The starter tariff begins at the Unix epoch, which is the code's way of
                        saying "always" and not a date anybody set. */}
                    <Td>
                      {Date.parse(tariff.effectiveFrom) <= 0
                        ? t('tariffs.fromTheStart')
                        : formats.moment(tariff.effectiveFrom)}
                    </Td>
                    <Td>
                      {tariff.effectiveTo === null ? (
                        <Pill tone="ok" soft>
                          {t('tariffs.inForce')}
                        </Pill>
                      ) : (
                        formats.moment(tariff.effectiveTo)
                      )}
                    </Td>
                    <Td numeric>{formats.money(tariff.baseFare)}</Td>
                    <Td numeric>{formats.money(tariff.perKilometre)}</Td>
                    <Td numeric>{formats.money(tariff.perMinute)}</Td>
                    <Td numeric>{formats.money(tariff.minimumFare)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <NewTariffDialog
          open={open}
          draft={draft}
          classes={classes}
          saving={saving}
          error={error?.message ?? null}
          onChange={change}
          onClose={() => setOpen(false)}
          onSubmit={() => void introduce()}
          onInvalid={setError}
        />
      </Section>
    </FieldErrors>
  );
}
